import React, { createContext, useContext, useState, useEffect } from 'react'
import { isTokenExpired } from '../services/authService'
import config from '../config/env'

interface AuthContextType {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
  embedId: string | null
  setEmbedId: (embedId: string | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null)
  const [embedId, setEmbedId] = useState<string | null>(null)

  // Cargar token del localStorage al inicializar
  useEffect(() => {
    const savedToken = localStorage.getItem(config.AUTH_TOKEN_KEY)
    const savedEmbedId = localStorage.getItem(config.AUTH_EMBED_ID_KEY)
    
    if (savedToken && !isTokenExpired(savedToken)) {
      setToken(savedToken)
    } else if (savedToken) {
      // Token expirado, limpiar
      localStorage.removeItem('etempo_token')
    }
    
    if (savedEmbedId) {
      setEmbedId(savedEmbedId)
    }
  }, [])

  // Guardar token en localStorage cuando cambie
  useEffect(() => {
    if (token) {
      localStorage.setItem('etempo_token', token)
    } else {
      localStorage.removeItem('etempo_token')
    }
  }, [token])

  // Guardar embedId en localStorage cuando cambie
  useEffect(() => {
    if (embedId) {
      localStorage.setItem('etempo_embedId', embedId)
    } else {
      localStorage.removeItem('etempo_embedId')
    }
  }, [embedId])

  const isAuthenticated = !!token && !isTokenExpired(token)

  const value: AuthContextType = {
    token,
    setToken,
    isAuthenticated,
    embedId,
    setEmbedId
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}